<template>
  <main>
    <div class="d-flex flex-row align-items-center justify-content-between px-4 py-3">
      <div>
        <h4 class="d-inline-block m-0 mr-4">CEX listing news trade</h4>
      </div>
      <div>
        <b-button variant="link" class="text-secondary mr-3" @click="onClickCancelAll">Cancel all</b-button>
        <b-button variant="outline-primary" v-b-modal:add-settings-modal>
          <b-icon-plus/> Place order
        </b-button>
      </div>
    </div>
    <table class="table table-hover">
      <thead>
      <tr>
        <th>Listing Exchange</th>
        <th>Mcap range (M$)</th>
        <th>Notional (USDT)</th>
        <th>TP/SL</th>
        <th>Max impact</th>
        <th>Trade account</th>
        <th>Trade market type</th>
        <th>Priority</th>
        <th></th>
      </tr>
      </thead>
      <draggable v-model="listingTradeSettings" item-key="id" ghost-class="bg-ghost" class="mb-3" tag="tbody" handle=".handle" @end="updatePriorities">
        <template #item="{ element: tradeSettings }">
          <tr>
            <td>
              <img :src="tradeSettings.listingExchangeLogo" :alt="tradeSettings.listingExchangeName" class="mr-1" style="width: 20px; height: 20px; object-fit: contain;">
              {{ tradeSettings.listingExchangeName }}
            </td>
            <td>{{ tradeSettings.displayMarketCapRange }}</td>
            <td>{{ tradeSettings.displayNotional }} ({{ tradeSettings.timeInForce.toUpperCase() }})</td>
            <td>
              <span class="text-success">{{ tradeSettings.takeProfitPercent || "--" }}%</span>
              /
              <span class="text-danger">{{ tradeSettings.stopLossPercent || "--" }}%</span>
            </td>
            <td>{{ tradeSettings.maxImpactPercent }}%</td>
            <td>
              <img :src="tradeSettings.cexAccountExchangeLogo" :alt="tradeSettings.cexAccountExchangeName" class="mr-1" style="width: 20px; height: 20px; object-fit: contain;">
              {{ tradeSettings.cexAccountLabel }}
            </td>
            <td>
              {{ tradeSettings.tradeExchange.endsWith("-futures") ? "USDT Perpetual" : "Spot" }}
            </td>
            <td>{{ tradeSettings.priority }}</td>
            <td>
              <b-button variant="link" size="sm" class="handle mr-2">
                <b-icon-chevron-bar-expand  />
              </b-button>
              <b-button variant="link" size="sm" class="mr-2" @click="onClickEdit(tradeSettings)">
                <b-icon-pencil />
              </b-button>
              <b-button variant="link" size="sm" class="text-danger" @click="onClickDelete(tradeSettings.id)">
                <b-icon-trash />
              </b-button>
            </td>
          </tr>
        </template>
      </draggable>
    </table>

    <b-modal :id="modalId" title="Place order" hide-footer no-fade @hidden="editObj = null;">
      <AddListingTradeSettingsModal :modalId="modalId" :tradeSettings="editObj" @done="fetchAll" />
    </b-modal>
  </main>
</template>

<style scoped lang="scss">
.bg-ghost {
  background-color: #fffeaf;
}
</style>

<script lang="ts">
import _ from "lodash";
import {defineComponent} from 'vue';
import * as cexAccountService from "@/services/cexAccountService";
import * as listingTradeSettingsService from "@/services/listingTradeSettingsService";
import draggable from "vuedraggable";
import * as constants from "@/constants.ts";
import BigNumber from "bignumber.js";
import AddListingTradeSettingsModal from "@/components/listing-trade/AddListingTradeSettingsModal.vue";

const numberFormatUsd = new Intl.NumberFormat("en", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

export default defineComponent({
  name: "ListingTradeSettings",
  components: {AddListingTradeSettingsModal, draggable},
  inject: ["toastError", "toastSuccess", "toastSuccessDelay", "showLoading", "hideLoading"],

  data() {
    return {
      modalId: "add-settings-modal",
      listingTradeSettings: [],
      isLoading: true,

      editObj: null,
    };
  },

  mounted() {
    document.title = "New CEX listing trade settings";
    this.fetchAll();
  },

  methods: {
    async fetchAll() {
      try {
        this.isLoading = true;
        this.listingTradeSettings = await listingTradeSettingsService.getAll();
        for (const tradeSettings of this.listingTradeSettings) {
          tradeSettings.listingExchangeLogo = constants.EXCHANGE_LOGO[tradeSettings.listingExchange];
          tradeSettings.listingExchangeName = tradeSettings.listingExchange === "upbit" ? "Upbit KRW" : constants.exchangeNames[tradeSettings.listingExchange];
          tradeSettings.cexAccountExchangeLogo = constants.EXCHANGE_LOGO[tradeSettings.cexAccountExchange];
          tradeSettings.cexAccountExchangeName = constants.exchangeNames[tradeSettings.cexAccountExchange];

          tradeSettings.displayMarketCapRange = [
            BigNumber(tradeSettings.marketCapMin).shiftedBy(-6).decimalPlaces(0).toFixed(),
            BigNumber(tradeSettings.marketCapMax).shiftedBy(-6).decimalPlaces(0).toFixed()
          ].join(" - ");
          tradeSettings.displayNotional = numberFormatUsd.format(tradeSettings.notional);
        }

      } catch (e) {
        console.error(e);
        this.toastError(e);

      } finally {
        this.isLoading = false;
      }
    },

    onClickEdit(obj) {
      this.editObj = obj;
      this.$bvModal.show(this.modalId);
    },

    async onClickDelete(id: number) {
      try {
        this.showLoading();
        await listingTradeSettingsService.cancel(id);
      } catch (e) {
        console.error(e);
        this.toastError(e);
      }

      this.hideLoading();
      this.fetchAll();
    },

    async onClickCancelAll() {
      try {
        this.showLoading();
        await listingTradeSettingsService.cancel();
      } catch (e) {
        console.error(e);
        this.toastError(e);
      }

      this.hideLoading();
      this.fetchAll();
    },

    async updatePriorities() {
      const priorities = this.listingTradeSettings.map((tradeSettings, index) => ({
        id: tradeSettings.id,
        priority: index
      }));

      try {
        this.showLoading();
        await listingTradeSettingsService.editPriorities(priorities);
      } catch (e) {
        console.error(e);
        this.toastError(e);
      }

      this.hideLoading();
      this.fetchAll();
    },
  },


});
</script>
